/**
 * @file: MaintenancePlanRemindBody.tsx
 *
 * `OrgMaintenancePlanRemind` — the cadence reminder for a
 * maintenance-plan detail. The largest body in the family because it
 * carries the most context: the plate / vehicle / spec line up top,
 * the plan-detail rows + due-date chip via `PlanDetailCard`, the
 * "last visit" block with order-no / dates / mileage, an inline link
 * to the source service-record, plus a three-dots actions menu that
 * opens the shared `MaintenancePlanBottomSheet` for in-place edit.
 *
 * The `daysUntilService` chip is re-derived from `nextServiceDate`
 * against today (date-fns) — the payload's snapshotted value goes
 * stale on old reminders.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { useAutoMarkRead } from '@euler/app/flows/feed/functions/useAutoMarkRead';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMaintenancePlanRemindFeedPayload } from '@euler/model/feed';
import { LeadCoverageInfo } from '@euler/model/lead/types';
import { formatDateTime } from '@euler/utils/formatters/datetime';
import { usePersistFn } from '@euler/utils/hooks';
import { getVehicleAgeByVin } from '@euler/utils/vin/getVehicleAgeByVin';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import parseISO from 'date-fns/parseISO';
import startOfToday from 'date-fns/startOfToday';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { LeadAffordanceButton } from './common/LeadAffordanceButton';
import {
  PlanDetailActionsButton,
  PlanDetailEditPatch,
} from './common/PlanDetailActionsButton';
import { PlanDetailCard } from './common/PlanDetailCard';
import { PlanDetailReasonSignature } from './common/PlanDetailReasonSignature';
import { abnormalLevelInfo, detailTypeName } from './common/labels';
import { isCoveredByLead } from './common/leadCovered';
import { canEditPlanDetail } from './common/planDetailEditable';
import { Chip, LabelRow, TitleText } from './common/primitives';

export const MaintenancePlanRemindBody: FC<{
  p: OrgMaintenancePlanRemindFeedPayload;
  /** Recipient-row id of this feed message (for manual mark-read). */
  messageId?: string;
  /** Read timestamp — absent/null means unread. */
  readAt?: string | null;
  /** Mark this message read once the user acts on it. */
  onMarkRead?: (recipientId: string) => void;
  /** Convert-to-lead bundle done — mark every bundled reminder read. */
  onConvertedToLead?: (planDetailIds: number[]) => void;
}> = ({ p, messageId, readAt, onMarkRead, onConvertedToLead }) => {
  const { rpx } = useResponsiveContext();
  const markRead = usePersistFn(() => {
    if (messageId) onMarkRead?.(messageId);
  });
  // Successful inline edits override the snapshot fields here so the
  // body stays consistent without waiting for a thread refetch.
  const [editedFields, setEditedFields] = useState<PlanDetailEditPatch | null>(
    null,
  );
  // Coverage is resolved by the lead affordance button (the feed snapshot
  // doesn't carry it); used to gate the edit menu. Optimistically uncovered
  // until it loads so the menu doesn't pop in late for the common case.
  const [coverage, setCoverage] = useState<LeadCoverageInfo | undefined>();
  // Turning this reminder into a lead IS the action that clears it — wherever
  // it happened (this card, the plans timeline, another device, the auto-lead
  // job). Without this the card would keep offering 标记为已读 next to its own
  // 查看商机 button. Fires once, as soon as the coverage lookup lands.
  useAutoMarkRead({
    handled: isCoveredByLead(coverage),
    messageId,
    readAt,
    onMarkRead,
  });
  // Alert toggles from the actions menu override the snapshot's flags here so
  // the 下次提醒 / 客户提醒 rows reflect the change without a thread refetch
  // (mirrors how the plans timeline refreshes its alert bells).
  const [alertOverride, setAlertOverride] = useState<{
    isAlertEnabled: boolean;
    isAlertEnabledForCustomer: boolean;
  } | null>(null);
  const planDetail = useMemo(
    () => ({
      ...p.planDetail,
      ...(editedFields ?? {}),
      ...(alertOverride ?? {}),
    }),
    [p.planDetail, editedFields, alertOverride],
  );
  const subjectName = planDetail.customAlertSubjectName ?? planDetail.name;
  // `p.daysUntilService` is snapshotted at write time, so an old feed
  // would render a stale "X 天后到期" forever (e.g. a 2024 reminder
  // viewed in 2026 would still claim "29 days until expire"). Re-derive
  // from the next-service date against today so the badge always
  // reflects the user's current relationship to the deadline.
  const days = differenceInCalendarDays(
    parseISO(planDetail.nextServiceDate),
    startOfToday(),
  );
  const dueColor = days < 0 ? '#ff3b30' : days <= 1 ? '#ff9500' : '#1f80ff';
  const dueText =
    days > 0
      ? `${days} 天后到期`
      : days === 0
      ? '今日到期'
      : `已过期 ${Math.abs(days)} 天`;
  const ab = abnormalLevelInfo(planDetail.abnormalLevel);
  // The server pre-collapses brand+model into `vehicleName` (deduped) so
  // we use it as-is here — don't re-prepend the brand or we double up
  // ("现代 现代 途胜").
  const vehicleDisplay = p.vehicleName ?? p.vehicleModel;
  // 8-char Chinese plates are new-energy (NEV) — fuel plates are 7
  // chars. Mirror the green accent used by `LicensePlateNoInput` so the
  // EV badge reads consistently across the app.
  const isNewEnergyPlate = p.licensePlateNo.length === 8;
  const plateColor = isNewEnergyPlate ? '#40c779' : '#1f80ff';
  const age = getVehicleAgeByVin(p.vin);
  const yearParts: string[] = [];
  if (p.vehicleModelYear) yearParts.push(`${p.vehicleModelYear} 款`);
  if (age > 0) yearParts.push(`车龄 ${age} 年`);
  const specParts = [
    p.vehicleDisplacement,
    p.vehicleFuelType,
    p.vehicleTrimLevel,
  ].filter((s): s is string => !!s);
  // Offer the edit menu only while the detail is still actionable — not yet a
  // lead, not past its service date. Coverage is optimistically uncovered
  // until the affordance resolves it.
  const canEdit = canEditPlanDetail({
    leadCoverage: coverage?.coverage ?? 'uncovered',
    nextServiceDate: planDetail.nextServiceDate,
  });
  return (
    <View>
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-wrap: wrap;
        `}
      >
        <TitleText>{subjectName}</TitleText>
        <View style={{ marginLeft: rpx(8) }}>
          <Chip text={detailTypeName(planDetail.type)} color="#6b7280" />
        </View>
        <View style={{ marginLeft: rpx(6) }}>
          <Chip text={ab.name} color={ab.color} />
        </View>
        {canEdit ? (
          <View style={{ marginLeft: 'auto' }}>
            <PlanDetailActionsButton
              planNo={p.refs.planNo}
              planDetailId={p.refs.planDetailId}
              detail={planDetail}
              onEdited={patch =>
                setEditedFields(prev => ({ ...(prev ?? {}), ...patch }))
              }
              onAlertChanged={setAlertOverride}
            />
          </View>
        ) : null}
      </View>
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-wrap: wrap;
          margin-top: ${rpx(6)}px;
        `}
      >
        <View
          css={`
            padding: ${rpx(1)}px ${rpx(6)}px;
            border-radius: ${rpx(4)}px;
            border-width: 1px;
            border-color: ${plateColor};
          `}
        >
          <Typography.Text
            size={rpx(12)}
            color={plateColor}
            medium
            letterSpacing={1}
          >
            {p.licensePlateNo}
          </Typography.Text>
        </View>
        <Typography.Text ml={rpx(8)} size={rpx(12)} color="#6b7280">
          {vehicleDisplay}
        </Typography.Text>
      </View>
      {yearParts.length > 0 || specParts.length > 0 ? (
        <Typography.Text mt={rpx(4)} size={rpx(12)} color="#6b7280">
          {[...yearParts, ...specParts].join(' · ')}
        </Typography.Text>
      ) : null}
      <PlanDetailCard
        detail={planDetail}
        nextServiceTrailing={<Chip text={dueText} color={dueColor} />}
      />
      <PlanDetailReasonSignature
        type={planDetail.type}
        unsolvedReason={planDetail.unsolvedReason}
        signatureImgUrl={planDetail.customerSignatureImgUrl}
      />
      {planDetail.isAlertEnabled && planDetail.nextAlertTime ? (
        <LabelRow
          label="下次提醒"
          value={formatDateTime(planDetail.nextAlertTime)}
        />
      ) : null}
      {planDetail.isAlertEnabledForCustomer &&
      planDetail.nextAlertTimeForCustomer ? (
        <LabelRow
          label="客户提醒"
          value={formatDateTime(planDetail.nextAlertTimeForCustomer)}
        />
      ) : null}
      <View
        css={`
          flex-direction: row;
          align-items: center;
          margin-top: ${rpx(10)}px;
        `}
      >
        <Typography.Text size={rpx(12)} color="#6b7280" medium>
          上次进店信息
        </Typography.Text>
        <View style={{ marginLeft: rpx(8) }}>
          <ServiceRecordLinkButton
            serviceRecordId={p.refs.serviceRecordId}
            taskNo={p.refs.taskNo}
          />
        </View>
      </View>
      {p.currentMileage ? (
        <LabelRow label="进店里程" value={`${p.currentMileage} km`} />
      ) : null}
      <LabelRow label="进店单号" value={p.refs.orderNo} />
      {p.orderedAt ? (
        <LabelRow label="进店时间" value={formatDateTime(p.orderedAt)} />
      ) : null}
      {p.finishedAt ? (
        <LabelRow label="完成时间" value={formatDateTime(p.finishedAt)} />
      ) : null}
      {p.customerName ? (
        <LabelRow label="车主信息" value={p.customerName} />
      ) : null}
      {p.sourceSaName ? (
        <LabelRow label="服务顾问" value={p.sourceSaName} />
      ) : null}
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-wrap: wrap;
        `}
      >
        <LeadAffordanceButton
          planDetailId={p.refs.planDetailId}
          onConverted={onConvertedToLead}
          onCoverageResolved={setCoverage}
        />
        {onMarkRead && messageId && !readAt ? (
          <MarkAsReadButton onPress={markRead} />
        ) : null}
      </View>
    </View>
  );
};

/**
 * Secondary outline CTA: clears this reminder from the inbox. Sits next
 * to the lead button — maintenance reminders only go read on an explicit
 * action (this, or converting to a lead), never on opening the thread.
 */
const MarkAsReadButton: FC<{ onPress: () => void }> = memo(({ onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      css={`
        align-self: flex-start;
        margin-top: ${rpx(12)}px;
        padding: ${rpx(6)}px ${rpx(14)}px;
        border-radius: ${rpx(6)}px;
        border-width: 1px;
        border-color: #d1d5db;
      `}
    >
      <Typography.Text size={rpx(13)} color="#6b7280" medium>
        标记为已读
      </Typography.Text>
    </TouchableOpacity>
  );
});

const ServiceRecordLinkButton: FC<{
  serviceRecordId?: string;
  taskNo: string;
}> = memo(({ serviceRecordId, taskNo }) => {
  const { rpx } = useResponsiveContext();
  const navigation = useAppNavigation();
  const color = '#555';
  const onPress = useCallback(() => {
    if (serviceRecordId) {
      navigation.navigate('ServiceRecord', { id: serviceRecordId });
    } else {
      navigation.navigate('Task', { taskNo });
    }
  }, [navigation, serviceRecordId, taskNo]);
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
    >
      <Typography.Text size={rpx(11)} color={color}>
        [服务记录 →]
      </Typography.Text>
    </TouchableOpacity>
  );
});
